import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSite } from '../context/SiteContext';
import { Layout } from '../components/layout/Layout';
import { ComicPanel, ComicHeading, ComicButton } from '../components/ComicPanel';
import { BookOpen, Gift, Bell, Zap, Settings, AlertCircle, PlayCircle, Loader2, Video, ScrollText, Flame, FileSearch, Map } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, userSite, updatePreferences, isVerified, token } = useAuth();
  const { isInteractiveLive } = useSite();
  const [continueReading, setContinueReading] = useState([]);
  const [featuredContent, setFeaturedContent] = useState([]);
  const [loadingProgress, setLoadingProgress] = useState(true);

  useEffect(() => {
    if (isVerified && token) {
      fetchContinueReading();
      fetchFeaturedContent();
    } else {
      setLoadingProgress(false);
    }
  }, [isVerified, token]);

  const fetchContinueReading = async () => {
    try {
      const response = await axios.get(`${API}/progress/continue-reading`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContinueReading(response.data);
    } catch (error) {
      console.error('Failed to fetch reading progress:', error);
    } finally {
      setLoadingProgress(false);
    }
  };
const fetchFeaturedContent = async () => {
  try {
    const sectionsToCheck = [
      'sporc-minute',
      'tob-skits',
      'piggee-pen-case-files',
      'sporc-sparks',
      'skits',
      'lore',
      'bonus'
    ];

    const requests = sectionsToCheck.map((section) =>
      axios.get(`${API}/content/library/${section}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
    );

    const responses = await Promise.all(requests);

    const featuredItems = responses
      .flatMap((response) => response.data || [])
      .filter((item) => item.isFeatured);

    setFeaturedContent(featuredItems);
  } catch (error) {
    console.error('Failed to fetch featured content:', error);
  }
};
  const handleUpdateNotifications = async (checked) => {
    try {
      await updatePreferences(checked);
      toast.success(checked ? 'Email updates enabled' : 'Email updates disabled');
    } catch (error) {
      toast.error('Failed to update preferences');
    }
  };

  const sections = [
  {
    title: 'Skits',
    description: 'Main episodes and Pork Cops stories',
    icon: BookOpen,
    to: '/library/skits',
    color: 'bg-red-500',
  },
  {
    title: 'TOB Skits',
    description: 'Tower of Beef shorts',
    icon: Video,
    to: '/library/tob-skits',
    color: 'bg-orange-500',
  },
  {
    title: 'Sporc Minute',
    description: 'Short Pork Cops explainers',
    icon: ScrollText,
    to: '/library/sporc-minute',
    color: 'bg-cyan-500',
  },
  {
    title: 'Sporc Sparks',
    description: 'Quick comedy clips',
    icon: Flame,
    to: '/library/sporc-sparks',
    color: 'bg-blue-500',
  },
  {
    title: 'Case Files',
    description: 'Piggee Pen investigations',
    icon: FileSearch,
    to: '/library/piggee-pen-case-files',
    color: 'bg-pink-500',
  },
  {
    title: 'Lore',
    description: 'Characters, places, and worldbuilding',
    icon: Map,
    to: '/library/lore',
    color: 'bg-green-500',
  },
  {
    title: 'Bonus',
    description: 'Behind the scenes & extras',
    icon: Gift,
    to: '/library/bonus',
    color: 'bg-yellow-500',
  },
  {
    title: 'Updates',
    description: 'News and announcements',
    icon: Bell,
    to: '/library/updates',
    color: 'bg-blue-500',
  },
  {
    title: 'Interactive',
    description: isInteractiveLive ? 'Choose your adventure' : 'Coming soon!',
    icon: Zap,
    to: isInteractiveLive ? '/library/interactive' : '/donate',
    color: 'bg-purple-500',
    disabled: !isInteractiveLive,
  },
];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Verification Warning */}
        {!isVerified && (
          <div className="mb-8 p-4 bg-yellow-500/10 border-2 border-yellow-500 flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-yellow-500 font-bold">Email not verified</p>
              <p className="text-slate-300 text-sm">
                Please verify your email to access members-only content.{' '}
                <Link to="/verify-required" className="underline hover:text-yellow-500">
                  Resend verification email
                </Link>
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
          <div>
            <ComicHeading as="h1" className="text-3xl sm:text-4xl" data-testid="dashboard-title">
              Members Dashboard
            </ComicHeading>
            <p className="text-slate-400 mt-2">Welcome back, {user?.email}</p>
          </div>
        </div>

        {/* Continue Reading Section */}
        {isVerified && (
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <PlayCircle className="w-6 h-6 text-yellow-500" />
              <ComicHeading as="h2" className="text-xl" data-testid="continue-reading-title">
                Continue Reading
              </ComicHeading>
            </div>

            {loadingProgress ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-yellow-500 animate-spin" />
              </div>
            ) : continueReading.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {continueReading.map((item) => (
                  <Link 
                    key={item.contentItem.id} 
                    to={`/read/${item.contentItem.slug}?page=${item.lastViewedImageIndex}`}
                    data-testid={`continue-${item.contentItem.slug}`}
                  >
                    <ComicPanel className="p-4 flex items-center gap-4 group">
                      <div className="w-16 h-20 bg-slate-700 overflow-hidden flex-shrink-0">
                        <img 
                          src={item.contentItem.imagePaths?.[0] || 'https://placehold.co/64x80/1e293b/F59E0B?text=?'}
                          alt={item.contentItem.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-bold truncate group-hover:text-yellow-500 transition-colors">
                          {item.contentItem.title}
                        </h3>
                        <p className="text-slate-400 text-sm">
                          Page {item.lastViewedImageIndex + 1} of {item.totalPages}
                        </p>
                        <div className="w-full bg-slate-700 h-1 mt-2 rounded-full overflow-hidden">
                          <div 
                            className="bg-yellow-500 h-full transition-all"
                            style={{ width: `${((item.lastViewedImageIndex + 1) / item.totalPages) * 100}%` }}
                          />
                        </div>
                      </div>
                    </ComicPanel>
                  </Link>
                ))}
              </div>
            ) : (
              <ComicPanel className="p-6 text-center">
                <p className="text-slate-400">No reading in progress. Start exploring content below!</p>
              </ComicPanel>
            )}
          </div>
        )}
{/* Featured Content */}
{isVerified && featuredContent.length > 0 && (
  <div className="mb-12">
    <div className="flex items-center gap-3 mb-6">
      <PlayCircle className="w-6 h-6 text-yellow-500" />
      <ComicHeading as="h2" className="text-xl">
        Featured Content
      </ComicHeading>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {featuredContent.map((item) => (
        <Link key={item.id} to={`/read/${item.slug}`}>
          <ComicPanel className="p-4 h-full group">
            <div className="aspect-video bg-slate-700 overflow-hidden mb-4">
              <img
                src={item.imagePaths?.[0] || 'https://placehold.co/640x360/1e293b/F59E0B?text=Pork+Cops'}
                alt={item.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                loading="lazy"
              />
            </div>

            <p className="text-yellow-500 text-xs uppercase font-bold tracking-widest mb-2">
              {item.section.replaceAll('-', ' ')}
            </p>

            <h3 className="text-white font-black uppercase text-lg group-hover:text-yellow-500 transition-colors">
              {item.title}
            </h3>

            {item.videoUrl && (
              <p className="text-slate-400 text-sm mt-2">Watch video</p>
            )}
          </ComicPanel>
        </Link>
      ))}
    </div>
  </div>
)}
        {/* Content Sections Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
          {sections.map((section) => (
            <Link 
              key={section.title} 
              to={section.to}
              className={section.disabled ? 'opacity-50' : ''}
              data-testid={`dashboard-tile-${section.title.toLowerCase()}`}
            >
              <ComicPanel className="p-6 h-full group">
                <div className={`w-12 h-12 ${section.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <section.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white font-black uppercase tracking-tight text-xl mb-2">
                  {section.title}
                </h3>
                <p className="text-slate-400 text-sm">{section.description}</p>
              </ComicPanel>
            </Link>
          ))}
        </div>

        {/* Settings Section */}
        <ComicPanel className="p-6" data-testid="dashboard-settings">
          <div className="flex items-center gap-3 mb-6">
            <Settings className="w-6 h-6 text-yellow-500" />
            <ComicHeading as="h2" className="text-xl">
              Settings
            </ComicHeading>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="email-updates" className="text-white font-bold">
                Email Updates
              </Label>
              <p className="text-slate-400 text-sm">
                Receive notifications about new content and updates
              </p>
            </div>
            <Switch
              id="email-updates"
              checked={userSite?.wantsUpdates ?? true}
              onCheckedChange={handleUpdateNotifications}
              data-testid="email-updates-toggle"
            />
          </div>
        </ComicPanel>
      </div>
    </Layout>
  );
};

export default Dashboard;
